import styled, { css } from "../../__compat__/styled-components/main.entry.mjs.mjs";
import { injectTypography } from "../../utils/injectTypography.mjs.mjs";
import { injectInteractionBar, injectInteractionBarActiveStyles, injectInteractionBarHoverStyles, interactionBarSelector, } from "../../utils/injectInteractionBar.mjs.mjs";
import { injectSpacing } from "../../api/spacing/injectSpacing.mjs.mjs";
import { getEasing } from "../../utils/getEasing.mjs.mjs";
import { injectOSXFontSmoothing } from "../../utils/injectOSXFontSmoothing.mjs.mjs";
var GridAreas;
(function (GridAreas) {
    GridAreas["label"] = "Label";
    GridAreas["input"] = "Input";
    GridAreas["clearFieldButton"] = "ClearFieldButton";
    GridAreas["extensionEnd"] = "ExtensionEnd";
})(GridAreas || (GridAreas = {}));
function injectInputLayout(props) {
    const layout = {
        gridTemplateAreas: `
      "${GridAreas.label}"
      "${GridAreas.input}"
    `,
    };
    if (props.hasExtensionEnd) {
        layout.gridTemplateAreas = `
      "${GridAreas.label} X"
      "${GridAreas.input} ${GridAreas.extensionEnd}"
    `;
        layout.gridTemplateColumns = '1fr auto';
    }
    if (props.hasClearFieldButton) {
        layout.gridTemplateAreas = `
    "${GridAreas.label} X"
    "${GridAreas.input} ${GridAreas.clearFieldButton}"
    `;
        layout.gridTemplateColumns = '1fr auto';
    }
    return layout;
}
const getStateColor = ({ theme, disabled, invalid, readOnly, filled }, state) => {
    const key = state ||
        (filled && 'filled') ||
        (disabled && 'disabled') ||
        (invalid && 'invalid') ||
        (readOnly && 'readOnly') ||
        'default';
    return {
        default: theme.colors.interaction.secondary.default,
        disabled: theme.colors.interaction.secondary.disabled,
        invalid: theme.colors.ui.error,
        readOnly: theme.colors.interaction.secondary.readonly,
        hover: theme.colors.interaction.secondary.hover,
        focus: theme.colors.interaction.secondary.focus,
        filled: theme.colors.interaction.secondary.filled,
    }[key];
};
const commonStyles = (props) => css `
  margin: 0;
  padding: 0;

  color: var(${getStateColor});
  ${injectTypography(props, 'copy1')}
  ${injectOSXFontSmoothing(true)}
  
  background-color: transparent;
  border: none;
  border-radius: 0;

  appearance: none;

  &:disabled {
    cursor: not-allowed;
  }
`;
export const StyledTextField = styled.div.withConfig({ displayName: "auir-611444a-StyledTextField", componentId: "auir-611444a-yv43e2" }) `
  ${injectSpacing('margin')}
`;
export const StyledTextFieldLayout = styled.div.withConfig({ displayName: "auir-611444a-StyledTextFieldLayout", componentId: "auir-611444a-297aux" }) `
  --text-field-indent-block-start: var(
    ${(props) => props.theme.responsive.spacing.xxs}
  );
  --text-field-indent-block-end: var(
    ${(props) => props.theme.responsive.spacing.xs}
  );
  --text-field-extension-indent: var(
    ${(props) => props.theme.responsive.spacing.xs}
  );

  display: grid;

  ${injectInputLayout}
`;
export const StyledInputWrapper = styled.div.withConfig({ displayName: "auir-611444a-StyledInputWrapper", componentId: "auir-611444a-143guaq" }) `
  /** 
   * This container is only here to give context to some CSS selectors like
   *':focus-within' and '::after'.
   */
  display: contents;

  ${(props) => !props.$readOnly &&
    css `
      ${injectInteractionBar(props)}

      &${interactionBarSelector} {
        grid-area: ${GridAreas.input};
        align-self: end;
      }
    `}
`;
export const StyledExtension = styled.div.withConfig({ displayName: "auir-611444a-StyledExtension", componentId: "auir-611444a-peycd5" }) `
  ${commonStyles}

  position: relative;

  display: inline-block;
  grid-area: ${GridAreas.extensionEnd};
  box-sizing: content-box;
  min-width: 24px;
  max-height: 25px; /*  includes 1px for the bottom border */
  padding-block-start: var(--text-field-indent-block-start);
  padding-block-end: var(--text-field-indent-block-end);
  padding-inline-start: var(--text-field-extension-indent);
  padding-inline-end: var(--text-field-extension-indent);

  text-align: end;

  cursor: ${(props) => {
    if (props.disabled) {
        return 'not-allowed';
    }
    if (props.onClick) {
        return 'pointer';
    }
    return false;
}};

  user-select: none;

  ${(props) => props.onClick &&
    css `
      &:hover {
        color: var(${getStateColor(props, 'hover')});
      }
    `}

  &:disabled {
    color: var(${(props) => getStateColor(props, 'disabled')});
  }

  ${(props) => !props.readOnly &&
    css `
      ${injectInteractionBar({ ...props, static: !props.onClick })}

      &${interactionBarSelector} {
        position: absolute;
        bottom: 0;
        left: 0;
      }

      /* Trigger interactionBar when preceding elements child received focus */
      *:focus-within ~ &${interactionBarSelector} {
        ${injectInteractionBarActiveStyles}
      }

      /* Trigger interactionBar when preceding elements child received hover */
      *:hover ~ &${interactionBarSelector} {
        ${injectInteractionBarHoverStyles}
      }
    `}
`;
export const StyledInput = styled.input.withConfig({ displayName: "auir-611444a-StyledInput", componentId: "auir-611444a-zeote2" }) `
  ${commonStyles}

  display: block;
  grid-area: ${GridAreas.input};
  box-sizing: border-box;
  width: 100%;

  padding-inline-start: 2px; /* Make sure the cursor is visible next to outline on focus */
  padding-inline-end: var(--text-field-extension-indent);
  padding-block-start: var(--text-field-indent-block-start);
  padding-block-end: var(--text-field-indent-block-end);
  margin-block-end: 1px; /* Adds the space for the interaction bar */

  transition: color 0.25s var(${getEasing});

  &:hover {
    color: var(${(props) => getStateColor(props, 'hover')});
  }

  &:focus {
    color: var(${(props) => getStateColor(props, 'focus')});
  }

  &:disabled {
    color: var(${(props) => getStateColor(props, 'disabled')});
  }

  &:invalid {
    box-shadow: none; /* Removes outline in Firefox */
  }
`;
export const StyledClearFieldButton = styled.div.withConfig({ displayName: "auir-611444a-StyledClearFieldButton", componentId: "auir-611444a-x2vk33" }) `
  ${commonStyles}
  position: relative;
  display: inline-block;
  grid-area: ${GridAreas.clearFieldButton};
  box-sizing: content-box;
  min-width: 24px;

  text-align: end;

  button {
    max-width: 24px;
    max-height: 24px;
    padding: 0;
  }

  cursor: ${(props) => {
    if (props.disabled) {
        return 'not-allowed';
    }
    if (props.onClick) {
        return 'pointer';
    }
    return false;
}};

  user-select: none;

  ${(props) => props.onClick &&
    css `
      &:hover {
        color: var(${getStateColor(props, 'hover')});
      }
    `}

  &:disabled {
    color: var(${(props) => getStateColor(props, 'disabled')});
  }

  ${(props) => !props.readOnly &&
    css `
      ${injectInteractionBar({ ...props, static: !props.onClick })}

      &${interactionBarSelector} {
        position: absolute;
        bottom: 0;
        left: 0;
      }

      /* Trigger interactionBar when preceding elements child received focus */
      *:focus-within ~ &${interactionBarSelector} {
        ${injectInteractionBarActiveStyles}
      }

      /* Trigger interactionBar when preceding elements child received hover */
      *:hover ~ &${interactionBarSelector} {
        ${injectInteractionBarHoverStyles}
      }
    `}
`;
//# sourceMappingURL=TextField.styles.mjs.mjs.map